import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import ClickOutside from 'app/src/elements/ClickListener';
import { customizableColumns, reorder, toggleHidden } from './preferences';

// Portalled and fixed rather than absolute: both tables sit in scroll
// containers that would clip the panel, and the post-sale table's sticky cells
// are at z-index 100.
const PANEL_STYLE = {
  position: 'fixed',
  zIndex: 1000,
  backgroundColor: '#fff',
  border: '1px solid lightgray',
  borderRadius: 4,
  padding: 12,
  minWidth: 220,
  maxHeight: '60vh',
  overflowY: 'auto',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
};

const ROW_STYLE = { display: 'flex', alignItems: 'center' };

// `columns` are the source columns in display order, never the ones with
// preferences applied — otherwise a column the user hid drops out of this list.
const ColumnPicker = ({ columns, preferences, onChange, onReset }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const available = customizableColumns(columns);
  const hidden = preferences.hidden || [];

  const anchorToButton = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();

    if (!rect) { return; }

    const next = { top: rect.bottom + 4, left: rect.left };

    // Most scrolls leave the button where it was — the controls row is sticky —
    // so keep the same object and let React skip the re-render.
    setPosition(current => (
      current.top === next.top && current.left === next.left ? current : next
    ));
  }, []);

  useEffect(() => {
    if (!open) { return undefined; }

    // Capture, so scrolling any ancestor container re-anchors the panel too.
    document.addEventListener('scroll', anchorToButton, true);
    window.addEventListener('resize', anchorToButton);

    return () => {
      document.removeEventListener('scroll', anchorToButton, true);
      window.removeEventListener('resize', anchorToButton);
    };
  }, [open, anchorToButton]);

  const toggleOpen = () => {
    anchorToButton();
    setOpen(!open);
  };

  const move = (index, offset) => {
    const to = index + offset;

    if (to < 0 || to >= available.length) { return; }

    onChange(reorder(preferences, columns, index, to));
  };

  const panel = (
    <div
      data-testid="column-picker-panel"
      style={{ ...PANEL_STYLE, top: position.top, left: position.left }}
    >
      {available.map((column, index) => (
        <div key={column.key} style={ROW_STYLE}>
          <input
            id={`column-toggle-${column.key}`}
            type="checkbox"
            checked={!hidden.includes(column.key)}
            onChange={() => onChange(toggleHidden(preferences, column.key))}
          />
          <label
            htmlFor={`column-toggle-${column.key}`}
            style={{ flexGrow: 1, paddingLeft: 6 }}
          >
            {column.label}
          </label>
          <button
            type="button"
            aria-label={`Move ${column.label} up`}
            onClick={() => move(index, -1)}
          >
            ↑
          </button>
          <button
            type="button"
            aria-label={`Move ${column.label} down`}
            onClick={() => move(index, 1)}
          >
            ↓
          </button>
        </div>
      ))}

      <button type="button" onClick={onReset}>Reset</button>
    </div>
  );

  return (
    <div data-testid="column-picker">
      <button ref={buttonRef} type="button" onClick={toggleOpen}>Columns</button>

      {open && createPortal(
        <ClickOutside exceptionRef={buttonRef} onClick={() => setOpen(false)}>
          {panel}
        </ClickOutside>,
        document.body
      )}
    </div>
  );
};

export default ColumnPicker;
